/**
 * @flow weak
 * @format
 */
/* eslint-disable flowtype/no-types-missing-file-annotation */
/* global __DEV__ */

import _ from 'lodash';
import { delay } from 'redux-saga';
import {
  put,
  call,
  all,
  takeLatest,
  take,
  race,
  select,
  spawn,
  fork,
} from 'redux-saga/effects';

import * as API from '~/services/api';
import { constants, actions } from '~/actions/design';
import {
  getValuesForEval,
  getCurrentStep,
  getFieldValues,
  getSchematic,
} from '~/selectors/design';
import { getChartData, getDesignName } from '~/selectors/global';
import { actions as errorActions } from '~/actions/errors';

import appActions from '~/routines/global';
import designActions from '~/routines/design';

import {
  isNumeric,
  countDecimals,
  asyncCancelOnTake,
  downloadBlob,
  downloadSVGImage,
  makeFileName,
} from '~/utils';
import createValidator from '~/utils/createValidator';
import { isNumber, isInteger } from '~/utils/validators';
// import Rollbar from '~/utils/rollbar';

import debug from '~/utils/macros/debug.macro';

const TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT);
const MAX_DECIMAL_PLACES = Number(process.env.REACT_APP_MAX_DECIMAL_PLACES);
const EVALUATE_DELAY = Number(process.env.REACT_APP_API_DELAY);

const evaluateActionType = {
  CHANGE: 'DATA_CHANGE',
  REFRESH: 'REFRESH',
  SAVE: 'AUTO_COMPLETE',
};

// hack :(
// TODO: Move to app-data.json
const integerFields = ['Num_Cpll', 'Num_pll_fet'];

const buildPayload = (
  actionType: ?string,
  state: any = {},
  type: string = evaluateActionType.CHANGE,
): any => {
  // Handle any step resets
  // if (actionType === constants.STEP_RESET) {
  //   console.log('test');
  // //   // These fields need to be omited so they reset properly.
  // //   const omitFields = ['DCRcust', 'Lcust', 'Irms_cust'];
  // //   const newState = _.omit(state, omitFields);
  //   return {
  //     actionEvent: {
  //       type: evaluateActionType.REFRESH,
  //     },
  //     data: buildData(newState, _.get(action, ['payload', 'field'])),
  //   };
  // }

  // const value: number = _.get(action, ['payload', 'value']);
  // let field: string = _.get(action, ['payload', 'field']);

  // Fix some wonky API issues - pretty hacky.
  // if (field === 'K_DeltaI') field = 'K_DeltaI_Entry';

  const data = _.reduce(
    state,
    (obj: any, val: any, key: string) => {
      const value = _.get(state[key], 'currentValue');
      return _.set(obj, key, isNumeric(value) ? Number(value) : value);
    },
    {},
  );

  const strType =
    actionType === constants.STEP_RESET ? evaluateActionType.REFRESH : type;
  return {
    actionEvent: { type: strType },
    data,
  };
};

function* errorHelper(err, message) {
  const statusCode: number = _.get(err, ['response', 'status'], '-1');
  if (statusCode === 401) {
    yield call(
      delay,
      Number(process.env.REACT_APP_API_REDIRECT_TIMEOUT) || 10000,
    );
    yield call(API.loginRedirect);
  } else {
    yield put(
      errorActions.apiError({
        status: statusCode.toString(),
        message,
      }),
    );
    // if (Rollbar) {
    //   Rollbar.critical(`API Error: ${err.message}`);
    // }
  }
}

export function getUrlParam(key) {
  const matches = window.location.href.match(
    new RegExp(`[?&]${key}=([^&#]+)\\b`),
  );
  return matches && matches.length > 0 ? matches[1] : null;
}

export function* fetchDesignData(): Generator<*, *, *> {
  try {
    yield put(appActions.scrollToTop());
    yield put(actions.pendingAction());
    const { response, error } = yield call(API.fetchDefaultData);
    if (!error) {
      // const fixedResponse = _.set(response, ['message', 'type'], 'SUCCESS');
      // console.log(response);
      yield put(actions.receiveData(response));
    }
  } catch (err) {
    yield call(errorHelper, err, 'API Error: Fetch design failed.');
  }
}

export function* evaluateReport(): Generator<*, *, *> {
  const state = yield select(getFieldValues);
  const payload = buildPayload(null, state);

  try {
    const result = yield race({
      evaluate: call(API.evaluateData, payload),
      timeout: call(delay, TIMEOUT),
    });

    if (_.get(result, 'timeout')) {
      yield put(
        actions.evaluateError({
          field: 'all',
          status: '-1',
          message: `Evaluation took longer than expected (<${TIMEOUT /
            1000}s).`,
        }),
      );

      // if (Rollbar) {
      //   Rollbar.warning(`Evaluation timeout (expected ${TIMEOUT / 1000}s).`, {
      //     ...payload,
      //   });
      // }
    } else {
      const { response } = _.get(result, 'evaluate', {});
      yield put(actions.receiveData(response));
    }
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield call(API.loginRedirect);
    } else {
      yield put(
        actions.evaluateError({
          field: 'all',
          status: _.get(err, ['response', 'status'], '-1'),
          message: _.get(err, ['message'], 'Unknown error'),
        }),
      );

      // if (Rollbar) {
      //   Rollbar.error(`Unexpected Evaluation Error: ${err.message}`, {
      //     ...payload,
      //   });
      // }
    }
  }
}

export function* evaluateData(action: any): Generator<*, *, *> {
  // Debounce this action by 500ms
  yield call(delay, EVALUATE_DELAY);

  const payloadData = yield select(getValuesForEval);
  const payload = buildPayload(
    action.type,
    payloadData,
    evaluateActionType.CHANGE,
  );
  const field: string = _.get(action, ['payload', 'field']);

  try {
    const result = yield race({
      evaluate: call(API.evaluateData, payload),
      timeout: call(delay, TIMEOUT),
    });

    if (_.get(result, 'timeout')) {
      yield put(
        actions.evaluateError({
          field,
          status: '-1',
          message: `Evaluation took longer than expected (<${TIMEOUT /
            1000}s).`,
        }),
      );

      // if (Rollbar) {
      //   Rollbar.warning(`Evaluation timeout (expected ${TIMEOUT / 1000}s).`, {
      //     ...payload,
      //   });
      // }
    } else {
      const { response } = _.get(result, 'evaluate', {});
      // fix message.type
      const fixedResponse = _.set(response, ['message', 'type'], 'SUCCESS');
      yield put(actions.receiveData(fixedResponse));
    }
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield call(API.loginRedirect);
    } else {
      yield put(
        actions.evaluateError({
          field,
          status: _.get(err, ['response', 'status'], '-1'),
          message: _.get(err, ['message'], 'Unknown error'),
        }),
      );
      // if (Rollbar) {
      //   Rollbar.error(`Unexpected Evaluation Error: ${err.message}`, {
      //     ...payload,
      //   });
      // }
    }
  }
}

export function* watchStepProgression(): Generator<*, *, *> {
  while (
    yield take([
      appActions.jumpToStep.toString(),
      constants.STEP_FORWARD,
      constants.AUTO_COMPLETE,
    ])
  ) {
    const currentStep = yield select(getCurrentStep);
    yield put(appActions.scrollToTop());

    if (currentStep === 'STEP_ANALYSIS') {
      const chartData = yield select(getChartData);

      if (!_.get(chartData, ['hasData'])) {
        yield put(appActions.fetchChartData());
      }
    } else if (currentStep === 'SCHEMATIC') {
      yield put(appActions.fetchSchematic());
    }
  }
}

export function* handleValidate(action: AppActions): Generator<*, *, *> {
  const field = _.get(action, ['payload', 'field']);
  const value = _.get(action, ['payload', 'value'], null);
  const isSelection = _.get(action, ['payload', 'isSelection'], false);
  // const revert = _.get(action, ['payload', 'revert'], false);
  const revert = action.type === constants.REVERT_FIELD;

  try {
    // passthrough for selection dropdowns/ tables
    if (isSelection || revert) {
      yield put(actions.evaluateData({ field, value }));
      yield call(evaluateData, action);
    } else {
      yield call(delay, 500);

      const validateChecker = integerFields.includes(field)
        ? isInteger
        : isNumber;
      // $FlowIgnore
      const validator = yield call(createValidator, validateChecker);

      let fixedValue = value;
      if (_.isString(value) && value[0] === '.') {
        fixedValue = `0${value}`;
      }

      // TODO: proper validator framekwork
      const decimalCount = countDecimals(fixedValue);
      if (decimalCount > MAX_DECIMAL_PLACES) {
        yield put(
          actions.validationError({
            field,
            message: `Must be less than ${MAX_DECIMAL_PLACES +
              1} decimal places.`,
          }),
        );
      } else {
        const valid = yield call(validator.test, fixedValue);
        const message = validator.report(value);

        if (valid) {
          yield put(actions.evaluateData({ field, value: fixedValue }));
          // $FlowIgnore
          yield spawn(
            asyncCancelOnTake,
            evaluateData,
            [action],
            constants.VALIDATION_ERROR,
          );
        } else {
          yield put(actions.validationError({ field, message }));
        }
      }
    }
  } catch (e) {
    // if (Rollbar) {
    //   Rollbar.critical('Unexpected Validation Error', { field, value });
    // }
    debug(e); // eslint-disable-line no-console
  }
}

export function* handleDownloadPdf(): Generator<*, *, *> {
  try {
    // yield put(actions.requestPDF());
    const payloadData = yield select(getValuesForEval);
    const payload = buildPayload(null, payloadData);

    const designName = yield select(getDesignName);

    // Include a saved design name for the generated PDF
    if (designName !== 'Untitled Design') {
      payload.data.design_name = {
        currentValue: designName,
      };
    }

    const result = yield call(API.downloadReportPDF, payload);
    const fileName = makeFileName(
      `${process.env.REACT_APP_TOOL_NAME}_${designName}`,
      'pdf',
    );
    // const fileName = `${_.snakeCase(`${process.env.REACT_APP_TOOL_NAME}_${designName}`)}.pdf`;
    yield put(actions.receivePDF(result, fileName));
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield call(API.loginRedirect);
    } else {
      yield put(
        actions.apiError({
          status: '-1',
          message: 'Fetch PDF Error',
          err,
        }),
      );
    }
  }
}

export function* downloadSchematic(): Generator<*, *, *> {
  try {
    const { data } = yield select(getSchematic);
    const designName = yield select(getDesignName);
    const fileName = makeFileName(
      `${process.env.REACT_APP_TOOL_NAME}_${designName}`,
    );
    downloadSVGImage(data, fileName);
  } catch (err) {
    yield put(
      actions.apiError({
        status: '-1',
        message: 'Schematic Download Error',
        err,
      }),
    );
  }
}

export function* downloadBOMCVS(): Generator<*, *, *> {
  try {
    const bomData = yield select(
      (state: AppStateType) => state.global.BOMTable,
    );
    const designName = yield select(getDesignName);
    const filename = makeFileName(
      `${process.env.REACT_APP_TOOL_NAME}_${designName}`,
      'xlsx',
    );

    // $FlowIgnore
    import('xlsx-populate/browser/xlsx-populate-no-encryption').then(xlsx => {
      const workbook = xlsx.fromBlankAsync().then(wb => {
        const sheet = wb.sheet(0);

        // Set the table data
        sheet.cell('A1').value(bomData);

        // http://polymathprogrammer.com/2010/01/18/calculating-column-widths-in-excel-open-xml/
        // eslint-disable-next-line prettier/prettier
        const getCellWidth = n => Math.trunc(((n * 13 + 20) / 13) * 256) / 256;

        // Set the column widths
        const len = bomData.length;
        const width = 5; // this should be taken from bomData[0]

        // Loop over each column and find the longest length string
        for (let i = 0; i < width; i += 1) {
          const col = String.fromCharCode(i + 65);

          // Get the range for this column and use the parse callback to format the sheet
          let maxLen = 0;
          // eslint-disable-next-line no-unused-vars
          sheet.range(`${col}1:${col}${len}`).value((cell, ri, ci) => {
            const val = cell.value();

            if (!val) return val;

            if (String(val).length > maxLen) {
              maxLen = val.length;
            }

            // Skip over the header cell for this row
            if (ri === 0) return val;

            if (col === 'B') {
              return Number(val);
            }

            // Check the partnumber and manufacturer columns and set hyperlinks
            if (col === 'C') {
              const nextVal = cell.relativeCell(0, 1).value();
              if (nextVal && nextVal.startsWith('ON')) {
                // Set ON Semi link here
                cell
                  .relativeCell(0, 1)
                  // .value(nextVal)
                  .style({
                    fontColor: '0563c1',
                    underline: true,
                  })
                  .hyperlink(`${process.env.REACT_APP_COMPANY_URL}`);

                // Set part number hyperlink
                cell
                  .style({
                    fontColor: '0563c1',
                    underline: true,
                  })
                  .hyperlink(
                    `${process.env.REACT_APP_COMPANY_URL}${
                      process.env.REACT_APP_PRODUCT_LINK
                    }${val}`,
                  );
              }
            }

            return val;
          });

          sheet.column(col).width(getCellWidth(maxLen));
        }

        // Set bold style on the first column and first row
        sheet.column('A').style('bold', true);
        sheet.row(1).style('bold', true);

        return wb.outputAsync({ type: undefined });
      });

      workbook.then(blob => downloadBlob(blob, filename));
    });
  } catch (err) {
    yield put(
      actions.apiError({
        status: '-1',
        message: 'XLSX Generation Error',
        err,
      }),
    );
  }
}

export function* handleSavePdf(action: any): Generator<*, *, *> {
  try {
    const file = new Blob([action.payload.response], {
      type: 'application/octet-stream',
    });
    yield call(downloadBlob, file, action.filename);
  } catch (err) {
    debug(err);
  }
}

export function* handleOpenDesign(): Generator<*, *, *> {
  try {
    yield put(appActions.scrollToTop());
    yield put(actions.receiveDesigns());
    yield put(designActions.fetchAllRecords());
  } catch (err) {
    yield call(errorHelper, err, 'Fetch All Designs Error');
  }
}

// export function* handleFetchDesign(action: any): Generator<*, *, *> {
//   try {
//     const result = yield call(API.fetchRecord, action.payload);
//     const payload = _.get(result, ['response'], {});
//     // yield put(actions.receiveData(payload));
//     // yield put(notify.openSuccess());
//   } catch (err) {
//     const statusCode: number = _.get(err, ['response', 'status']);
//     if (statusCode === 401) {
//       yield call(API.loginRedirect);
//     } else {
//       yield put(
//         actions.apiError({
//           status: '-1',
//           message: 'Fetch All Designs Error',
//         }),
//       );
//     }
//   }
// }

export function* handleSaveDesign(action: AppActions): Generator<*, *, *> {
  const recordName = _.get(action, 'payload', '');
  const state = yield select(getFieldValues);
  const plData = buildPayload('save', state);

  const payload = {
    actionEvent: { recordName, type: plData.actionEvent.type },
    data: plData.data,
  };

  try {
    const result = yield race({
      evaluate: call(API.saveDesignRecord, payload),
      timeout: call(delay, TIMEOUT),
    });

    if (_.get(result, 'timeout')) {
      yield put(
        actions.apiError({
          status: '-1',
          message: `Save Design took longer than expected (<${TIMEOUT /
            1000}s).`,
        }),
      );

      // if (Rollbar) {
      //   Rollbar.warning(`Save Design timeout (expected ${TIMEOUT / 1000}s).`, {
      //     ...payload,
      //   });
      // }
      // } else {
      // yield put(notify.saveSuccess());
    }
  } catch (err) {
    const statusCode: number = _.get(err, ['response', 'status']);
    if (statusCode === 401) {
      yield call(API.loginRedirect);
    } else {
      yield put(
        actions.apiError({
          status: '-1',
          message: 'Save Design Error',
        }),
      );

      // if (Rollbar) {
      //   Rollbar.error(`Unexpected error saving design: ${err.message}`, {
      //     ...payload,
      //   });
      // }
    }
  }
}

/**
 * Generic helper for making API calls and building a payload from the design state.
 *
 * @private
 * @param {Function} apiFn Service API function
 * @param {Routine} routineFn Saga Routine function
 * @param {string} action Action type
 * @param {(string[] | (() => boolean))} [cb=[]] Cancel Callback
 * @returns Iterator
 */

function* helperCallApi(
  apiFn,
  routineFn = () => {},
  action = null,
  cb?: string[] | (() => boolean) = [],
) {
  let result = {};

  function* fetchData(data) {
    yield put(routineFn.request());
    const res = yield call(apiFn, data);

    console.log(res);
    /**
     * Non connection related API specfic errors have a type = 'ERROR'
     * and need to be handled here as they are not necessarily caught
     * in the try/catch of `helperCallApi`.
     */
    if (res && res.response && res.response.type === 'ERROR') {
      yield put(routineFn.failure(res.response.content));
      return;
    }
    yield put(routineFn.success(res));
  }

  try {
    let cancelFn;
    let finalPayload: { [key: string]: string } = {};

    // TODO: shouldn't be modifying the payload here
    const actionType = action && action.type;

    switch (actionType) {
      case designActions.saveDesignRecord.TRIGGER: {
        const payloadData = yield select(getFieldValues);
        finalPayload = buildPayload('save', payloadData);
        finalPayload.actionEvent.recordName = action.payload;
        break;
      }

      case designActions.updateRecordName.TRIGGER:
      case designActions.copyDesign.TRIGGER:
      case designActions.deleteDesign.TRIGGER:
      case 'FETCH_DESIGN/TRIGGER': {
        finalPayload = action.payload;
        break;
      }
      default: {
        const payloadData = yield select(getValuesForEval);
        finalPayload = buildPayload(null, payloadData);

        if (actionType === designActions.updateDesign.TRIGGER) {
          finalPayload.id = action.payload.id;
        }
        break;
      }
    }

    if (typeof cb === 'function') {
      cancelFn = cb;
    } else {
      const a = [].concat(
        [
          appActions.jumpToStep.toString(),
          constants.STEP_FORWARD,
          constants.STEP_BACKWARD,
        ],
        cb,
      );
      cancelFn = cancelAction => a.includes(cancelAction.type);
    }

    result = yield race({
      task: call(fetchData, finalPayload),
      cancel: take(cancelFn),
    });
  } catch (err) {
    yield put(routineFn.failure(err.message));
    yield call(
      errorHelper,
      err,
      `API call for ${routineFn} was not completed.`,
    );
  } finally {
    console.log(result);
    yield put(routineFn.fulfill({ cancelled: result.cancel !== undefined }));
  }
}

function helperAPICallRouter(type) {
  const routine = appActions[type] || designActions[type];
  const fn = API[type];

  if (__DEV__) {
    /* eslint-disable no-console */
    if (!type || !routine) {
      console.warn(
        `Invalid or missing Routine for '${type}' (check 'routines/**')`,
      );
    }
    if (typeof fn !== 'function') {
      console.warn(
        `API function for '${type}' missing (check 'services/api.js')`,
      );
    }
    /* eslint-enable no-console */
  }

  // Not sure why I can't return the helper from here.. but we need an intemediary function and I don't
  // have time to figure out why.
  function* handler(action) {
    debug(`${routine} calling ${fn}`);
    yield helperCallApi(fn, routine, action);
  }

  return takeLatest(routine.toString(), handler);
}

export function* watchDesignActions(): Generator<*, *, *> {
  yield all([
    takeLatest(constants.NEW_DESIGN, fetchDesignData),
    takeLatest(
      [constants.CHANGE_VALUE, constants.REVERT_FIELD],
      handleValidate,
    ),
    takeLatest(constants.STEP_RESET, evaluateData),
    takeLatest(constants.REQUEST_PDF, handleDownloadPdf),
    takeLatest(constants.EXPORT_BOM_EXCEL, downloadBOMCVS),
    takeLatest(constants.DOWNLOAD_SCHEMATIC, downloadSchematic),
    takeLatest(constants.RECEIVE_PDF, handleSavePdf),
    // takeLatest(constants.SAVE_DESIGN, handleSaveDesign),
    takeLatest(constants.OPEN_DESIGN, handleOpenDesign),
    // takeLatest(constants.FETCH_DESIGN, handleFetchDesign),
    // takeLatest(constants.REQUEST_USER, fetchUserData),
    // takeLatest(constants.REQUEST_PARTS, fetchPartsData),

    helperAPICallRouter('fetchChartData'),
    helperAPICallRouter('fetchSchematic'),
    helperAPICallRouter('fetchAllRecords'),
    helperAPICallRouter('fetchRecord'),

    // TODO: fix payload being passed to the save design handler :/
    helperAPICallRouter('saveDesignRecord'),
    helperAPICallRouter('updateRecordName'),
    helperAPICallRouter('copyDesign'),
    helperAPICallRouter('deleteDesign'),
    helperAPICallRouter('updateDesign'),
  ]);
}

export default function* initializeDesignActions(): Generator<*, *, *> {
  yield* watchDesignActions();
  yield fork(watchStepProgression);

  // Add sagas or actions that need to be execuated after  inital setup here
  const initialActions = all([
    put(actions.newDesign(true)), // silent = true so the inital load doesn't hit GA
  ]);

  // Support redux-devtool's presist feature - only fire initial
  // actions if off, or in production.
  if (process.env.NODE_ENV === 'production') {
    yield initialActions;
  } else {
    // eslint-disable-next-line no-lonely-if
    if (!getUrlParam('debug_session')) {
      yield initialActions;
    }
  }
}



// WEBPACK FOOTER //
// ./src/sagas/design.js